import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2,
  FileText,
  Users,
  Settings as SettingsIcon,
  Save,
  CheckCircle2,
  Loader2,
  Upload,
  Mail,
  Phone,
  MapPin,
  Hash,
  DollarSign,
  Clock,
  Globe,
  Shield,
  Bell,
  Database,
  Eye,
  EyeOff
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [settings, setSettings] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    tax_id: '',
    default_tax_rate: 15.0,
    invoice_prefix: 'INV',
    proposal_prefix: 'EST',
    payment_terms: '',
    currency: 'CAD',
    date_format: 'YYYY-MM-DD',
    timezone: 'America/Halifax',
    email_notifications: true,
  });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'Staff'
  });

  useEffect(() => {
    fetchSettings();
    fetchUsers();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/auth/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const autoSave = async (field, value) => {
    setSaving(true);
    try {
      const updateData = { [field]: value };
      await api.put('/settings', updateData);
      setSettings({ ...settings, ...updateData });
      setLastSaved(new Date());
      toast.success('Settings saved', { duration: 2000 });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleInputBlur = (field, value) => {
    if (settings[field] !== value) {
      autoSave(field, value);
    }
  };

  const handleSwitchChange = (field, checked) => {
    handleInputChange(field, checked);
    autoSave(field, checked);
  };

  const handleCreateUser = async () => {
    // Validation
    if (!userForm.email || !userForm.password || !userForm.first_name || !userForm.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await api.post('/auth/register', userForm);
      console.log('User created:', response.data);
      toast.success('User created successfully');
      setUserDialogOpen(false);
      resetUserForm();
      // Fetch users after a short delay to ensure backend has processed
      setTimeout(() => {
        fetchUsers();
      }, 500);
    } catch (error) {
      console.error('Error creating user:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await api.put(`/auth/users/${userId}`, updates);
      toast.success('User updated successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/auth/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const resetUserForm = () => {
    setUserForm({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'Staff'
    });
    setEditingUser(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
              <p className="text-slate-600 mt-1">Manage your application settings and preferences</p>
            </div>
            <div className="flex items-center gap-3">
              {saving && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </div>
              )}
              {lastSaved && !saving && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Saved {new Date(lastSaved).toLocaleTimeString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="container mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="company" className="gap-2">
              <Building2 className="h-4 w-4" />
              Company
            </TabsTrigger>
            <TabsTrigger value="invoicing" className="gap-2">
              <FileText className="h-4 w-4" />
              Invoice & Proposals
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              User Management
            </TabsTrigger>
            <TabsTrigger value="system" className="gap-2">
              <SettingsIcon className="h-4 w-4" />
              System
            </TabsTrigger>
          </TabsList>

          {/* Company Settings */}
          <TabsContent value="company" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>Basic information about your company</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={settings.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      onBlur={(e) => handleInputBlur('company_name', e.target.value)}
                      placeholder="Cleanifax Inc."
                    />
                  </div>

                  <div>
                    <Label htmlFor="tax_id">Tax ID / Business Number</Label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="tax_id"
                        value={settings.tax_id}
                        onChange={(e) => handleInputChange('tax_id', e.target.value)}
                        onBlur={(e) => handleInputBlur('tax_id', e.target.value)}
                        className="pl-10"
                        placeholder="123456789"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="company_email">Company Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="company_email"
                        type="email"
                        value={settings.company_email}
                        onChange={(e) => handleInputChange('company_email', e.target.value)}
                        onBlur={(e) => handleInputBlur('company_email', e.target.value)}
                        className="pl-10"
                        placeholder="info@cleanifax.ca"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="company_phone">Company Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="company_phone"
                        value={settings.company_phone}
                        onChange={(e) => handleInputChange('company_phone', e.target.value)}
                        onBlur={(e) => handleInputBlur('company_phone', e.target.value)}
                        className="pl-10"
                        placeholder="+1 (902) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="col-span-full">
                    <Label htmlFor="company_address">Company Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <Textarea
                        id="company_address"
                        value={settings.company_address}
                        onChange={(e) => handleInputChange('company_address', e.target.value)}
                        onBlur={(e) => handleInputBlur('company_address', e.target.value)}
                        className="pl-10"
                        rows={3}
                        placeholder="123 Main Street, Halifax, NS B3H 1A1, Canada"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tax Settings</CardTitle>
                <CardDescription>Configure default tax rates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-md">
                  <Label htmlFor="default_tax_rate">Default HST/Tax Rate (%)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="default_tax_rate"
                      type="number"
                      step="0.01"
                      value={settings.default_tax_rate}
                      onChange={(e) => handleInputChange('default_tax_rate', parseFloat(e.target.value))}
                      onBlur={(e) => handleInputBlur('default_tax_rate', parseFloat(e.target.value))}
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Standard HST rate in Nova Scotia is 15%</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoice & Proposal Settings */}
          <TabsContent value="invoicing" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Numbering & Prefixes</CardTitle>
                <CardDescription>Configure document numbering formats</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="invoice_prefix">Invoice Number Prefix</Label>
                    <Input
                      id="invoice_prefix"
                      value={settings.invoice_prefix}
                      onChange={(e) => handleInputChange('invoice_prefix', e.target.value)}
                      onBlur={(e) => handleInputBlur('invoice_prefix', e.target.value)}
                      placeholder="INV"
                    />
                    <p className="text-xs text-slate-500 mt-1">Example: INV-2024-001</p>
                  </div>

                  <div>
                    <Label htmlFor="proposal_prefix">Proposal/Estimate Prefix</Label>
                    <Input
                      id="proposal_prefix"
                      value={settings.proposal_prefix}
                      onChange={(e) => handleInputChange('proposal_prefix', e.target.value)}
                      onBlur={(e) => handleInputBlur('proposal_prefix', e.target.value)}
                      placeholder="EST"
                    />
                    <p className="text-xs text-slate-500 mt-1">Example: EST-2024-001</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Payment Terms</CardTitle>
                <CardDescription>Default payment terms for invoices</CardDescription>
              </CardHeader>
              <CardContent>
                <Label htmlFor="payment_terms">Default Payment Terms</Label>
                <Textarea
                  id="payment_terms"
                  value={settings.payment_terms}
                  onChange={(e) => handleInputChange('payment_terms', e.target.value)}
                  onBlur={(e) => handleInputBlur('payment_terms', e.target.value)}
                  rows={4}
                  placeholder="Payment is due within 30 days of invoice date. Late payments may incur interest charges."
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Management */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>User Accounts</CardTitle>
                    <CardDescription>Manage users and their roles</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setUserDialogOpen(true)}
                    className="bg-[#247C75] hover:bg-[#1d6259]"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleUpdateUser(user.id, { role: value })}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Admin">Admin</SelectItem>
                              <SelectItem value="Manager">Manager</SelectItem>
                              <SelectItem value="Staff">Staff</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.is_active ? "default" : "secondary"}>
                            {user.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateUser(user.id, { is_active: !user.is_active })}
                            >
                              {user.is_active ? "Deactivate" : "Activate"}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id, user.email)}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Settings */}
          <TabsContent value="system" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Regional Settings</CardTitle>
                <CardDescription>Configure date, time, and currency formats</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="currency">Currency</Label>
                    <Select
                      value={settings.currency}
                      onValueChange={(value) => {
                        handleInputChange('currency', value);
                        autoSave('currency', value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select
                      value={settings.timezone}
                      onValueChange={(value) => {
                        handleInputChange('timezone', value);
                        autoSave('timezone', value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/Halifax">Atlantic Time (Halifax)</SelectItem>
                        <SelectItem value="America/Toronto">Eastern Time (Toronto)</SelectItem>
                        <SelectItem value="America/Vancouver">Pacific Time (Vancouver)</SelectItem>
                        <SelectItem value="America/New_York">Eastern Time (New York)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="date_format">Date Format</Label>
                    <Select
                      value={settings.date_format}
                      onValueChange={(value) => {
                        handleInputChange('date_format', value);
                        autoSave('date_format', value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2024-01-15)</SelectItem>
                        <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (01/15/2024)</SelectItem>
                        <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (15/01/2024)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Notifications</CardTitle>
                <CardDescription>Configure email notification preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Email Notifications</Label>
                    <p className="text-sm text-slate-500">Receive email notifications for important updates</p>
                  </div>
                  <Switch
                    checked={settings.email_notifications}
                    onCheckedChange={(checked) => handleSwitchChange('email_notifications', checked)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="user_email">Email *</Label>
              <Input
                id="user_email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="user@cleanifax.ca"
                required
              />
            </div>

            <div>
              <Label htmlFor="user_password">Password *</Label>
              <div className="relative">
                <Input
                  id="user_password"
                  type={showPassword ? "text" : "password"}
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="user_first_name">First Name *</Label>
                <Input
                  id="user_first_name"
                  value={userForm.first_name}
                  onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                  placeholder="John"
                  required
                />
              </div>

              <div>
                <Label htmlFor="user_last_name">Last Name *</Label>
                <Input
                  id="user_last_name"
                  value={userForm.last_name}
                  onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="user_role">Role</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                  <SelectItem value="Staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser}
              className="bg-[#247C75] hover:bg-[#1d6259]"
            >
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
